/**
 * Created by mariushirjanu on 10/06/2020.
 */
import * as _ from 'underscore';
import { Component, Input, SimpleChanges, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { CatalogStateService } from '../catalog-state.service';
import { trackByFunction } from '../../../../shared/formly/ga-formly.util';
import { CatalogOverviewService } from './catalog-overview.service';
import { IObject } from '../../../../../type_definitions/datatypes';
import { ResizeService } from '../../../../shared/services/resize.service';
import { BreakpointsService } from '../../../../shared/services/breakpoints.service';
import { OrderCatalogService } from '../order-catalog.service';
import { ICatalogProduct } from '../order-catalog-data.service';
import { MAIN_CONTAINER_ELEMENT } from '../../../main.component';

@Component({
  selector: 'catalog-overview-layout-block',
  template: `
    <ul class="catalog-layout catalog-layout-block" [ngClass]="{'without-images': withoutImages}">
      <li class="clearfix" *ngFor="let product of products; trackBy: trackByFunction" [scrollTarget]="getScrollTarget(product.id)">
        <div class="last">
          <div class="price">
            <span class="margin-bottom-sm" *ngIf="product.price.amount && !hidePrices">{{formatAmount(product.price.amount)}}</span>
            <span *ngIf="showLease && product.unitLeasePrice?.amount" translate [translateParams]="{ amount: formatAmount(product.unitLeasePrice?.amount), period: product.leasePeriod }">
              {{hidePrices ? 'CATALOG.DETAIL.LEASE_INFO_SHORT' : 'CATALOG.DETAIL.LEASE_INFO'}}
            </span>
          </div>
          <h2 class="item-title"><a [uiSref]="orderDetailsSref" [uiParams]="getOrderDetailsSrefParams(product.id)">{{product.title}}</a></h2>
          <div class="row align-items-center w-100 no-margin-left">
            <div class="col-auto ps-0">
              <a [uiSref]="orderDetailsSref" [uiParams]="getOrderDetailsSrefParams(product.id)" class="col-auto badge partnumber">{{product.part}}</a>
            </div>
            <div class="col-auto" [ngClass]="{'ms-auto pe-0': showLease}">
              <div class="stock" *ngIf="showStock && (product.stock || product.stock === 0)">
                <span translate>CATALOG.HEADERS.STOCK_COUNT</span>: {{product.stock}}
              </div>
              <favourite-toggle [productId]="product.id" [isFavourite]="product.favourite">
                <span translate>CATALOG.DETAIL.FAVOURITE_TOGGLE</span>
              </favourite-toggle>
            </div>
          </div>
          <div class="description" [innerHTML]="product.description"></div>
          <div class="lead-time" *ngIf="product.leadTime?.days" [ngClass]="{'warning-field': product.leadTime?.warning}">
            <span *ngIf="product.leadTime?.warning"><i class="fa fa-warning"></i></span>
            <span translate [translateParams]="{days: product.leadTime?.text}">CATALOG.DETAIL.LEAD_TIME</span>
          </div>
          <div class="warning-field">
            <div *ngIf="product.warning">
              <i class="fa fa-warning"></i><span>{{product.warning}}</span>
            </div>
            <div *ngIf="product.EOL?.warning">
              <i class="fa fa-warning"></i>
              <span translate [translateParams]="{date: product.EOL.date}">CATALOG.DETAIL.EOL_WARNING</span>
            </div>
          </div>
          <div class="clearfix">
            <div class="detail-link">
              <a [uiSref]="orderDetailsSref" [uiParams]="getOrderDetailsSrefParams(product.id)">
                <span translate>CATALOG.DETAIL.PRODUCT_DETAILS</span>
                <i class="fa fa-angle-right"></i>
              </a>
              <a *ngIf="product.compatibleProducts" [uiSref]="overviewCompatibleSref" [uiParams]="getOverviewCompatibleSrefParams(product.id)" [uiOptions]="{inherit: false}">
                <span translate>CATALOG.DETAIL.COMPATIBLE_PRODUCTS</span>
                <i class="fa fa-angle-right"></i>
              </a>
            </div>
            <add-to-cart [product]="product" [catalogView]="true"></add-to-cart>
          </div>
        </div>
        <div class="first" *ngIf="product.image">
          <a [uiSref]="orderDetailsSref" [uiParams]="getOrderDetailsSrefParams(product.id)">
            <img class="product-img" [src]="smallScreen ? '' : product.image.medium" alt="{{product.image.title}}"/>
          </a>
        </div>
      </li>
    </ul>
  `
})
export class CatalogOverviewLayoutBlockComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public products:ICatalogProduct[];
  @Input() public compatibleProductId:number;
  @Input() public showStock:boolean;
  @Input() public hidePrices:boolean;
  @Input() public showLease:boolean;

  public orderDetailsSref:string;
  public overviewCompatibleSref:string;
  public withoutImages:boolean;
  public trackByFunction:Function = trackByFunction;
  public formatAmount:Function = this.orderCatalogService.formatAmount;
  public getOrderDetailsSrefParams:Function = (productId:number) => this.catalogOverviewService.getOrderDetailsSrefParams(productId, this.compatibleProductId);
  public smallScreen:boolean;

  constructor(
    private catalogStateService:CatalogStateService,
    private catalogOverviewService:CatalogOverviewService,
    private resizeService:ResizeService,
    private breakpointsService:BreakpointsService,
    private orderCatalogService:OrderCatalogService
) {
    /***/
  }

  public ngOnInit(){
    this.activate();
    this.listen();
  }

  private activate():void {
    this.orderDetailsSref = this.catalogStateService.detailState;
    this.overviewCompatibleSref = this.catalogStateService.overviewState;
    this.sizeUpdate();
  }

  private listen(){
    this.resizeService.addResizeEventListener(MAIN_CONTAINER_ELEMENT, () => {
      this.sizeUpdate();
    });
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.products?.currentValue) {
      this.products = changes.products.currentValue;
      this.withoutImages = !(_.find(this.products, (product:any) => product.images?.length));
    }
  }

  public getScrollTarget(productId:number):string {
    return `product-${productId}`;
  }

  public getOverviewCompatibleSrefParams = (productId:number):IObject => {
    // add cat->null to always set category to All when compatible products clicked from layout-block
    return {
      compat: productId,
      cat: null
    };
  };

  private sizeUpdate():void {
    const currentBP:string = this.breakpointsService.current();
    this.smallScreen = (currentBP === 'xs' || currentBP === 'sm');
  }

  public ngOnDestroy():void {
    this.resizeService.removeResizeEventListener(MAIN_CONTAINER_ELEMENT);
  }
}